/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil1Icon } from '@radix-ui/react-icons';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import {
  MobileDetailHeader, MobileDetailAction,
} from '@/shared/ui/components/composed/mobile-sheet';
import type { ClientRow } from '../../context/ClientManagementContext';
import { useClientPerformance } from '../../hooks/useClientPerformance';
import { useSocialChatWidget, EMPTY_LEAD_ORIGIN } from '../../context/SocialChatWidgetContext';
import { hasUnseenSocialUpdate, markSocialSeen } from '@/shared/utils/social-lead-activity';
import { ClientDetailHeader } from './ClientDetailHeader';
import { ClientChannelStrip } from './ClientChannelStrip';
import { ClientPerformanceStrip } from './ClientPerformanceStrip';
import { ClientDetailTabBar } from './ClientDetailTabBar';
import { ClientDetailSidebar } from './ClientDetailSidebar';
import { ClientMobileActionBar } from './ClientMobileActionBar';
import type { ClientDetailTab } from './client-detail-tabs';
import type { ClientFormSection } from './client-quality';

export type { ClientDetailTab } from './client-detail-tabs';

export interface ClientDetailShellProps {
  client: ClientRow;
  agencyId: string;
  activeTab: ClientDetailTab;
  onTabChange: (tab: ClientDetailTab) => void;
  onNavigateToList: () => void;
  onEdit: (section?: ClientFormSection) => void;
  onCreateDeal: () => void;
  /** Opens the given attendance's detail page. */
  onViewAttendance: (dealId: string) => void;
  children: ReactNode;
}

/**
 * The client page's chrome, in the same shape as the property sheet:
 * header, the band of channels, the band of counts, then two columns —
 * tabs and their panel on the left, the right rail beside them.
 *
 * The shell owns the scrolling rather than the page: the app shell locks
 * `html` and `body` at `overflow: hidden`, so a root that counts on the
 * page scrolling never scrolls at all and everything below the fold
 * becomes unreachable without a single console error.
 *
 * @param props - The client, the active tab, the callbacks, and the tab panel.
 * @returns The rendered screen.
 */
export function ClientDetailShell({
  client,
  agencyId,
  activeTab,
  onTabChange,
  onNavigateToList,
  onEdit,
  onCreateDeal,
  onViewAttendance,
  children,
}: ClientDetailShellProps) {
  // Read once here rather than in each consumer: the band and the tab
  // counts are the same figures, and two readings of a paginated history
  // would have let them disagree.
  const perf = useClientPerformance(agencyId, client.client_id, client.registration_date);
  const { t } = useTranslation('features/clients');
  const isMobile = useIsMobile();
  /* Drives the detail header's shadow; see `MobileDetailHeader`. */
  const [scrolled, setScrolled] = useState(false);

  /*
   * The phone reaches the social panel from the bar, not from the round
   * launcher that floats over the corner: that button landed on top of the
   * bar it shares the corner with, and an icon alone was about to stop
   * saying which conversation it opened. Same store the launcher uses, so
   * the panel does not care which of the two raised it.
   */
  const openChat = useSocialChatWidget((s) => s.openChat);
  const openSocial = () => {
    markSocialSeen(client.client_id);
    openChat({
      agencyId,
      clientId: client.client_id,
      clientName: client.full_name,
      leadOrigin: client.lead_origin ?? EMPTY_LEAD_ORIGIN,
      leadInteractions: client.lead_interactions,
      contacts: client.contacts,
    });
  };

  /*
   * A WhatsApp contact, not just any phone: a number typed into the phone
   * field is not a promise that it is on WhatsApp, and sending somebody to a
   * dead conversation is worse than not offering the button.
   */
  const whatsAppNumber = client.contacts?.find((c) => c.type === 'whatsapp')?.value;
  const whatsAppHref = whatsAppNumber
    ? `https://wa.me/${whatsAppNumber.replace(/\D/g, '')}`
    : undefined;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-(--surface-primary)">
      {/* The screen's own bar, in place of the shell's. See the property
          sheet's header for why both of the shell's bars step aside here. */}
      {isMobile && (
        <MobileDetailHeader
          title={client.full_name}
          onBack={onNavigateToList}
          backLabel={t('detail.breadcrumb')}
          scrolled={scrolled}
          actions={(
            <MobileDetailAction
              label={t('detail.header.editRegistration')}
              testId="client-detail-edit"
              onClick={() => onEdit()}
            >
              <Pencil1Icon width={18} height={18} />
            </MobileDetailAction>
          )}
        />
      )}
      <div
        data-testid="client-detail-view"
        className="flex min-h-0 flex-1 flex-col gap-(--space-4) overflow-y-auto p-(--space-5)"
        onScroll={(e) => {
          const next = e.currentTarget.scrollTop > 4;
          setScrolled((current) => (current === next ? current : next));
        }}
      >
        <ClientDetailHeader
          client={client}
          agencyId={agencyId}
          onNavigateToList={onNavigateToList}
          onEdit={() => onEdit()}
          onCreateDeal={onCreateDeal}
          onViewAttendance={onViewAttendance}
        />

        <ClientChannelStrip client={client} onEdit={() => onEdit()} />

        <ClientPerformanceStrip perf={perf} onOpenTab={onTabChange} />

        {/*
          Hoisted out of the grid column on a phone: a sticky element only
          travels inside its own containing block, and that column is a
          fraction of the scroll. Pinned in there, the tabs slid away with
          the column the moment it passed.
        */}
        {isMobile && (
          <ClientDetailTabBar
            activeTab={activeTab}
            onTabChange={onTabChange}
            counts={{
              propertiesOfInterest: perf.interests,
              visits: perf.visits,
              proposals: perf.proposals,
              history: perf.interactions,
            }}
          />
        )}

        <div className="grid grid-cols-[minmax(0,1fr)_minmax(260px,340px)] items-start gap-(--space-5) max-[1100px]:grid-cols-1">
          <div className="flex min-w-0 flex-col gap-(--space-4)">
            {!isMobile && <ClientDetailTabBar
              activeTab={activeTab}
              onTabChange={onTabChange}
              counts={{
                propertiesOfInterest: perf.interests,
                visits: perf.visits,
                proposals: perf.proposals,
                history: perf.interactions,
              }}
            />}
            {children}
          </div>

          <ClientDetailSidebar
            client={client}
            agencyId={agencyId}
            onEdit={onEdit}
            onViewAttendance={onViewAttendance}
          />
        </div>
      </div>

      {/*
        A flex sibling of the scrolling box, so it holds the bottom without
        `position: fixed` — which would anchor to a viewport this content
        does not scroll within.
      */}
      {isMobile && (
        <ClientMobileActionBar
          whatsAppHref={whatsAppHref}
          onScheduleVisit={onCreateDeal}
          onOpenSocial={openSocial}
          hasUnseenSocial={hasUnseenSocialUpdate(client.client_id, client.lead_interactions)}
        />
      )}
    </div>
  );
}
